import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(url && anonKey);

// When the env vars are missing (local dev without a .env.local, CI/E2E with
// VITE_E2E_BYPASS_AUTH=true, or a misconfigured preview) we MUST NOT call
// createClient("", "", ...) — recent @supabase/supabase-js throws
// "supabaseUrl is required" synchronously at that call, which crashes the
// whole React tree before any bypass/fallback can run.
//
// Instead, expose a Proxy that throws only if code actually touches the
// client. The AuthProvider's E2E bypass and the receipt store's localStorage
// fallback both avoid touching `supabase` in that mode, so the proxy stays
// inert.
function createStubClient(): SupabaseClient {
  const err = () => {
    throw new Error(
      "Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
    );
  };
  const handler: ProxyHandler<object> = {
    get(_target, prop) {
      if (prop === "then") return undefined; // don't pretend to be a Promise
      err();
    },
  };
  return new Proxy({}, handler) as unknown as SupabaseClient;
}

export const supabase: SupabaseClient = isSupabaseConfigured
  ? createClient(url as string, anonKey as string, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : createStubClient();

if (!isSupabaseConfigured && typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.warn(
    "Supabase env vars missing. Using stub client; auth and persistence are disabled.",
  );
}
